// Wire protocol between editor clients (browser, agents) and the document server.

import type { DocumentJSON } from './document.ts';
import type { NodeId, Rect, Transaction } from './types.ts';

export interface Presence {
  clientId: string;
  name: string;
  color: string;
  kind: 'human' | 'agent';
  pageId?: NodeId;
  cursor?: { x: number; y: number } | null;
  selection?: NodeId[];
  /** Visible page rect (page coordinates). */
  viewport?: Rect;
  /** What the participant is doing right now (agents), e.g. "Creating 12 nodes". */
  status?: string;
  /** Latest emoji reaction (FigJam): shown to everyone as a short burst at that point. */
  reaction?: { emoji: string; x: number; y: number; at: number };
  /** Spotlight: everyone else follows this person's view. */
  spotlight?: boolean;
  /** In the file's audio call (and whether their mic is muted). */
  audio?: { muted: boolean } | null;
}

export interface CommentReply {
  id: string;
  author: string;
  /** the account that wrote it (servers with accounts), so only they or editors can change it */
  authorId?: string;
  authorKind: 'human' | 'agent';
  color: string;
  text: string;
  createdAt: number;
}

/** A comment thread pinned to a page point (optionally anchored to a node). */
export interface Comment extends CommentReply {
  pageId: NodeId;
  x: number;
  y: number;
  /** When set, the pin follows this node (x/y are relative to its top-left). */
  nodeId?: NodeId;
  resolved?: boolean;
  replies: CommentReply[];
}

export type CommentAction =
  | { action: 'add'; pageId: NodeId; x: number; y: number; nodeId?: NodeId; text: string }
  | { action: 'reply'; commentId: string; text: string }
  | { action: 'resolve'; commentId: string; resolved: boolean }
  | { action: 'move'; commentId: string; x: number; y: number; nodeId?: NodeId }
  | { action: 'delete'; commentId: string; replyId?: string };

/** A FigJam-style voting session: everyone gets N votes to place on layers. */
export interface VotingSession {
  id: string;
  title: string;
  pageId: NodeId;
  startedBy: string;
  votesPerPerson: number;
  active: boolean;
  startedAt: number;
  endedAt?: number;
  votes: { nodeId: NodeId; voter: string; color: string }[];
}

export type VoteAction =
  | { action: 'start'; pageId: NodeId; votesPerPerson?: number; title?: string }
  | { action: 'cast'; nodeId: NodeId }
  | { action: 'retract'; nodeId: NodeId }
  | { action: 'end' }
  | { action: 'clear' };

/** Votes per layer, most first. */
export function voteResults(s: VotingSession): { nodeId: NodeId; count: number; voters: string[] }[] {
  const m = new Map<NodeId, string[]>();
  for (const v of s.votes) m.set(v.nodeId, [...(m.get(v.nodeId) ?? []), v.voter]);
  return [...m.entries()].map(([nodeId, voters]) => ({ nodeId, count: voters.length, voters })).sort((a, b) => b.count - a.count);
}

export type ClientMessage =
  | { type: 'tx'; seq: number; tx: Transaction }
  | { type: 'presence'; presence: Partial<Presence> }
  | { type: 'comment'; op: CommentAction }
  | { type: 'vote'; op: VoteAction }
  /** WebRTC signaling for audio calls, relayed to one peer in the same file. */
  | { type: 'rtc'; to: string; data: unknown }
  | { type: 'resync' };

export type ServerMessage =
  /**
   * The file as of joining. Big documents come as `docUrl` instead (fetched once over HTTP, shared
   * by everyone joining at that version) when the client asked for that with `snapshot=1`.
   */
  | { type: 'init'; clientId: string; fileId: string; doc: DocumentJSON; docUrl?: string; peers: Presence[]; version: number; comments: Comment[]; role?: 'owner' | 'editor' | 'viewer'; voting?: VotingSession | null }
  | { type: 'comments'; comments: Comment[] }
  | { type: 'voting'; session: VotingSession | null }
  | { type: 'tx'; tx: Transaction; version: number }
  | { type: 'ack'; seq: number; version: number }
  | { type: 'reject'; seq: number; error: string }
  | { type: 'presence'; presence: Presence }
  | { type: 'leave'; clientId: string }
  /** Ask clients to bring nodes into view (agents use this to show their work). */
  | { type: 'focus'; ids: NodeId[]; pageId?: NodeId; select?: boolean; from: string }
  | { type: 'notice'; message: string; from: string }
  | { type: 'rtc'; from: string; data: unknown }
  /** Server-configured ICE servers for audio calls (none: same network only). */
  | { type: 'ice'; servers: { urls: string | string[]; username?: string; credential?: string }[] };

export const PRESENCE_COLORS = ['#f24822', '#ffa629', '#14ae5c', '#0d99ff', '#9747ff', '#ff24bd', '#0fa5a5', '#e8a900'];
